// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { Button, Chip } from '@mui/material';
import { Translation } from '@shared/ui-kit/providers/translation-provider/translation';
import { type FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useDocumentCatalog } from '../../hooks/use-document-catalog';
import {
  countActiveDrawerFilters,
  type DocumentDrawerFilters,
} from '../../types/document-filters';

export interface DocumentsActiveFiltersProps {
  readonly filters: DocumentDrawerFilters;
  readonly onRemoveFilter: (key: keyof DocumentDrawerFilters) => void;
  readonly onClearAll: () => void;
}

export const DocumentsActiveFilters: FC<DocumentsActiveFiltersProps> = ({
  filters,
  onRemoveFilter,
  onClearAll,
}) => {
  const { t } = useTranslation();
  const { categoryLabels, languageLabels, subcategoryLabels } =
    useDocumentCatalog();

  if (countActiveDrawerFilters(filters) === 0) {
    return null;
  }

  return (
    <div
      className="mb-4 flex flex-wrap items-center gap-2"
      data-testid="documents-active-filters"
    >
      {filters.category ? (
        <Chip
          label={t('documents.active-filters.category', {
            ns: 'components',
            label: categoryLabels[filters.category] ?? filters.category,
          })}
          size="small"
          onDelete={() => onRemoveFilter('category')}
          data-testid="documents-active-filter-category"
        />
      ) : null}

      {filters.subcategory ? (
        <Chip
          label={t('documents.active-filters.subcategory', {
            ns: 'components',
            label: subcategoryLabels[filters.subcategory] ?? filters.subcategory,
          })}
          size="small"
          onDelete={() => onRemoveFilter('subcategory')}
          data-testid="documents-active-filter-subcategory"
        />
      ) : null}

      {filters.language ? (
        <Chip
          label={t('documents.active-filters.language', {
            ns: 'components',
            label: languageLabels[filters.language] ?? filters.language,
          })}
          size="small"
          onDelete={() => onRemoveFilter('language')}
          data-testid="documents-active-filter-language"
        />
      ) : null}

      {filters.document_date_from ? (
        <Chip
          label={t('documents.active-filters.document-date-from', {
            ns: 'components',
            date: filters.document_date_from,
          })}
          size="small"
          onDelete={() => onRemoveFilter('document_date_from')}
          data-testid="documents-active-filter-document-date-from"
        />
      ) : null}

      {filters.document_date_to ? (
        <Chip
          label={t('documents.active-filters.document-date-to', {
            ns: 'components',
            date: filters.document_date_to,
          })}
          size="small"
          onDelete={() => onRemoveFilter('document_date_to')}
          data-testid="documents-active-filter-document-date-to"
        />
      ) : null}

      {filters.expiry_date_from ? (
        <Chip
          label={t('documents.active-filters.expiry-date-from', {
            ns: 'components',
            date: filters.expiry_date_from,
          })}
          size="small"
          onDelete={() => onRemoveFilter('expiry_date_from')}
          data-testid="documents-active-filter-expiry-date-from"
        />
      ) : null}

      {filters.expiry_date_to ? (
        <Chip
          label={t('documents.active-filters.expiry-date-to', {
            ns: 'components',
            date: filters.expiry_date_to,
          })}
          size="small"
          onDelete={() => onRemoveFilter('expiry_date_to')}
          data-testid="documents-active-filter-expiry-date-to"
        />
      ) : null}

      <Button
        variant="text"
        color="secondary"
        onClick={onClearAll}
        className="min-w-0 shrink-0 normal-case"
        data-testid="documents-active-filters-clear-all"
      >
        <Translation i18nKey="documents.active-filters.clear-all" ns="components" />
      </Button>
    </div>
  );
};
